import { Ingredient, IngredientMovement, MovementType } from '@/types/ingredient.types';
import { authFetch } from './api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export interface IngredientFilters {
  search?: string;
  category?: string;
  sortBy?: 'name' | 'category' | 'value' | 'supplier';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  includeInactive?: boolean;
}

export interface IngredientStats {
  totalItems: number;
  totalValue: number;
  lowStockItems: number;
  outOfStockItems: number;
  categories: number;
}

export interface IngredientResponse {
  success: boolean;
  data: Ingredient[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  stats: IngredientStats;
}

export interface CreateIngredientData {
  name: string;
  description?: string;
  category: string;
  unit: string;
  supplier?: string;
  globalCostPerUnit?: number;
  locationStocks?: Array<{
    locationId: string;
    locationName: string;
    currentStock: number;
    lastRestockDate?: string;
    expirationDate?: string;
    isActive: boolean;
  }>;
}

export interface StockAdjustmentData {
  locationId?: string;
  type: MovementType;
  quantity: number;
  reason: string;
  reference?: string;
}

export interface IngredientCategory {
  id: string;
  name: string;
  description: string;
}

export interface IngredientUnit {
  id: string;
  name: string;
  abbreviation: string;
}

export interface PriceRecord {
  id: string;
  ingredientId: string;
  price: number;
  date: string;
  effectiveDate: string;
  reason?: string;
  userId?: string;
  userName?: string;
}

class IngredientsService {
  private buildQueryParams(filters: IngredientFilters): string {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.category && filters.category !== 'all') params.append('category', filters.category);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.includeInactive) params.append('includeInactive', 'true');

    return params.toString();
  }

  /**
   * Obtener todos los ingredientes con filtros y paginación
   */
  async getIngredients(filters: IngredientFilters = {}): Promise<IngredientResponse> {
    const queryParams = this.buildQueryParams(filters);
    const url = `${API_BASE_URL}/ingredients${queryParams ? `?${queryParams}` : ''}`;

    const response = await authFetch(url, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener ingredientes');
    }

    return await response.json();
  }

  /**
   * Obtener un ingrediente por ID
   */
  async getIngredientById(id: string): Promise<{ success: boolean; data: Ingredient }> {
    const response = await authFetch(`${API_BASE_URL}/ingredients/${id}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener ingrediente');
    }

    return await response.json();
  }

  /**
   * Crear nuevo ingrediente
   */
  async createIngredient(data: CreateIngredientData): Promise<{ success: boolean; data: Ingredient; message: string }> {
    const response = await authFetch(`${API_BASE_URL}/ingredients`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al crear ingrediente');
    }

    return await response.json();
  }

  /**
   * Actualizar ingrediente existente
   */
  async updateIngredient(id: string, data: Partial<CreateIngredientData>): Promise<{ success: boolean; data: Ingredient; message: string }> {
    const response = await authFetch(`${API_BASE_URL}/ingredients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al actualizar ingrediente');
    }

    return await response.json();
  }

  /**
   * Eliminar ingrediente (soft delete)
   */
  async deleteIngredient(id: string): Promise<{ success: boolean; message: string }> {
    const response = await authFetch(`${API_BASE_URL}/ingredients/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al eliminar ingrediente');
    }

    return await response.json();
  }

  /**
   * Ajustar stock de ingrediente
   */
  async adjustStock(id: string, adjustment: StockAdjustmentData): Promise<{
    success: boolean;
    data: { ingredient: Ingredient; movement: IngredientMovement };
    message: string
  }> {
    const response = await authFetch(`${API_BASE_URL}/ingredients/${id}/stock-adjustment`, {
      method: 'POST',
      body: JSON.stringify(adjustment)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al ajustar stock');
    }

    return await response.json();
  }

  /**
   * Obtener movimientos de un ingrediente
   */
  async getIngredientMovements(
    id: string,
    filters: {
      page?: number;
      limit?: number;
      type?: MovementType | 'all';
      dateFrom?: string;
      dateTo?: string;
    } = {}
  ): Promise<{
    success: boolean;
    data: IngredientMovement[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.type && filters.type !== 'all') params.append('type', filters.type);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);

    const queryParams = params.toString();
    const url = `${API_BASE_URL}/ingredients/${id}/movements${queryParams ? `?${queryParams}` : ''}`;

    const response = await authFetch(url, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener movimientos');
    }

    return await response.json();
  }

  /**
   * Obtener categorías disponibles
   */
  async getCategories(): Promise<{ success: boolean; data: IngredientCategory[] }> {
    const response = await authFetch(`${API_BASE_URL}/ingredients/meta/categories`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener categorías');
    }

    return await response.json();
  }

  /**
   * Obtener unidades disponibles
   */
  async getUnits(): Promise<{ success: boolean; data: IngredientUnit[] }> {
    const response = await authFetch(`${API_BASE_URL}/ingredients/meta/units`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener unidades');
    }

    return await response.json();
  }

  /**
   * Buscar ingredientes por nombre (para autocompletado)
   */
  async searchIngredients(query: string, limit = 10): Promise<{ success: boolean; data: Ingredient[] }> {
    const params = new URLSearchParams({
      search: query,
      limit: limit.toString()
    });

    const response = await authFetch(`${API_BASE_URL}/ingredients?${params.toString()}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al buscar ingredientes');
    }

    const result = await response.json();
    return {
      success: result.success,
      data: result.data || []
    };
  }

  /**
   * Obtener ingredientes con stock bajo
   */
  async getLowStockIngredients(locationId?: string): Promise<{ success: boolean; data: Ingredient[] }> {
    const params = new URLSearchParams();
    if (locationId) params.append('locationId', locationId);

    // Usar el endpoint principal con un filtro especial para stock bajo
    const response = await authFetch(`${API_BASE_URL}/ingredients?lowStock=true&${params.toString()}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener ingredientes con stock bajo');
    }

    const result = await response.json();

    // Filtrar ingredientes con stock bajo (stock = 0) del lado del cliente
    const lowStockIngredients = result.data?.filter((ingredient: Ingredient) =>
      ingredient.locationStocks?.some(stock => stock.currentStock === 0)
    ) || [];

    return {
      success: true,
      data: lowStockIngredients
    };
  }

  /**
   * Obtener historial de precios de un ingrediente
   */
  async getPriceHistory(id: string): Promise<{ success: boolean; data: PriceRecord[] }> {
    const response = await authFetch(`${API_BASE_URL}/ingredients/${id}/price-history`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al obtener historial de precios');
    }

    return await response.json();
  }

  /**
   * Actualizar precio de un ingrediente
   */
  async updatePrice(
    id: string,
    price: number,
    effectiveDate: string,
    reason?: string,
    supplierId?: string
  ): Promise<{
    success: boolean;
    data: Ingredient;
    priceRecord: any;
    message: string
  }> {
    const response = await authFetch(`${API_BASE_URL}/ingredients/${id}/update-price`, {
      method: 'POST',
      body: JSON.stringify({
        price,
        effectiveDate,
        reason,
        supplierId
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || 'Error al actualizar precio');
    }

    return await response.json();
  }
}

export const ingredientsService = new IngredientsService();
